import { StoreRegion } from "@/lib/builder/builder.config";

export type AccountLinkType = {
	sectionName: string;
	link: {
		text: string;
		href: string;
	};
	roles: string[];
	functionality: string;
};

const links = [
	{
		sectionName: "Account Overview",
		link: {
			text: "Account Overview",
			href: "account",
		},
		roles: [],
		functionality: null,
	},
	{
		sectionName: "My Orders",
		link: {
			text: "My Orders",
			href: "account/orders",
		},
		roles: ["get_orders", "get_order_detail"],
		functionality: "orders",
	},
	{
		sectionName: "Invoices",
		link: {
			text: "Invoices",
			href: "account/invoices",
		},
		roles: ["get_invoices", "get_invoice_detail"],
		functionality: "invoices",
	},
	{
		sectionName: "My Lists",
		link: {
			text: "My Lists",
			href: "account/lists",
		},
		roles: ["get_shopping_lists", "get_shopping_list_detail"],
		functionality: "shoppingLists",
	},
	{
		sectionName: "Addresses",
		link: {
			text: "Addresses",
			href: "account/addresses",
		},
		roles: ["get_addresses", "get_address_detail"],
		functionality: "addressBook",
	},
	{
		sectionName: "Account Settings",
		link: {
			text: "Account Settings",
			href: "account/account-details",
		},
		roles: [],
		functionality: "accountSettings",
	},
	{
		sectionName: "Recently Viewed",
		link: {
			text: "Recently Viewed",
			href: "account/recently-viewed",
		},
		roles: [],
		functionality: "recentlyViewed",
	},
	{
		sectionName: "Quick Order",
		link: {
			text: "Quick Order",
			href: "account/quick-order",
		},
		roles: ["purchase_enable"],
		functionality: "quickOrderPad",
	},
	{
		sectionName: "User Management",
		link: {
			text: "User Management",
			href: "account/user-management",
		},
		roles: ["get_users"],
		functionality: "userManagement",
	},
	{
		sectionName: "Log Out",
		link: {
			text: "Log Out",
			href: "logout",
		},
		roles: [],
		functionality: null,
	},
] as AccountLinkType[];

/**
 * Filters the account links based on the given role.
 *
 * @param {UserData} sessionData - The role to filter the links by.
 * @return {AccountLinkType[]} An array of account links that match the given role.
 */
export const getAccountLinks = (
	rolePermissions: { actions: string[] },
	accountConfigSettings: any,
	options?: { store: StoreRegion; lang: string },
) => {
	if (!rolePermissions || !accountConfigSettings) {
		return links
			.filter((link) => link.roles.length === 0)
			.map((link) => {
				const href = options
					? `/${options.store}/${options.lang}/${link.link.href}`
					: link.link.href;

				return {
					...link,
					link: {
						...link.link,
						href,
					},
				};
			});
	}

	const permissions = rolePermissions?.actions;
	// filter the links down based on whats inside of the accountConfigSettings
	const filterdFunctionalityLinks = links.filter((link) => {
		if (link.functionality === null) {
			return true;
		}

		if (
			typeof accountConfigSettings.storefrontConfig.config[
				link.functionality
			] !== "undefined"
		) {
			return accountConfigSettings.storefrontConfig.config[link.functionality];
		}

		if (
			accountConfigSettings.storefrontConfig.config.invoice &&
			accountConfigSettings.storefrontConfig.config.invoice.value !== undefined
		) {
			return accountConfigSettings.storefrontConfig.config.invoice.value;
		}

		return true;
	});

	const filteredLinks = filterdFunctionalityLinks.filter((link) => {
		if (link.roles.length === 0) {
			return true;
		}

		return link.roles.some((role) => {
			// Check if any permission contains the role before the "|"
			return permissions.some((permission) => {
				// Split the permission string into [role, level]
				const [permissionRole, permissionLevel] = permission.split("|");
				// Check if the role matches the permission role
				return permissionRole === role;
			});
		});
	});

	if (!options) {
		return filteredLinks;
	}

	return filteredLinks.map((link) => {
		const linksObj = {
			...link,
			link: {
				...link.link,
				href: `/${options.store}/${options.lang}/${link.link.href}`,
			},
		};
		return linksObj;
	});
};

// ALL AVAILABLE ROLES
// 'get_addresses',
// 'get_address_detail',
// 'get_default_shipping',
// 'get_default_billing',
// 'create_address',
// 'update_address',
// 'delete_address',
// 'get_invoices',
// 'get_invoice_detail',
// 'export_invoices',
// 'get_invoice_pdf',
// 'get_invoice_payments_history',
// 'pay_invoice',
// 'get_orders',
// 'get_order_detail',
// 'get_quotes',
// 'get_quote_detail',
// 'create_quote',
// 'update_quote_message',
// 'checkout_with_quote',
// 'get_quote_pdf',
// 'get_shopping_lists',
// 'get_shopping_list_detail',
// 'create_shopping_list',
// 'deplicate_shopping_list',
// 'update_shopping_list',
// 'approve_draft_shopping_list',
// 'delete_shopping_list',
// 'create_shopping_list_item',
// 'update_shopping_list_item',
// 'delete_shopping_list_item',
// 'get_users',
// 'get_user_detail',
// 'create_user',
// 'update_user',
// 'delete_user',
// 'get_roles',
// 'get_role_detail',
// 'create_role',
// 'update_role',
// 'delete_role',
// 'purchase_enable'
